import { observable, syncState } from "@legendapp/state";
import { useValue } from "@legendapp/state/react";
import { ScrollFade } from "@repo/ui/components/scroll-fade";
import { SidebarMenu } from "@repo/ui/ui/sidebar";
import { Skeleton } from "@repo/ui/ui/skeleton";

import { boards$ } from "~/stores/boards";
import { datasets$ } from "~/stores/datasets";
import { threads$ } from "~/stores/threads";
import { BoardItem } from "./boards-list";
import { DatasetItem } from "./datasets-list";
import { ThreadItem } from "./threads-list";

/** Favorited boards, sorted by their fractional `order` key. */
const favoriteBoardIds$ = observable<string[]>(() => {
  const boards = boards$.get() ?? {};
  return Object.values(boards)
    .filter((board) => board.favorite)
    .sort((a, b) => {
      const orderA = a.order ?? "a0";
      const orderB = b.order ?? "a0";
      return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
    })
    .map((board) => board.id);
});

/** Favorited datasets (private or shared), sorted by their `order` key. */
const favoriteDatasetIds$ = observable<string[]>(() => {
  const datasetIds = Object.keys(datasets$);
  return datasetIds
    .filter((id) => datasets$[id].favorite.get())
    .sort((a, b) => {
      const orderA = datasets$[a].order.get() ?? "a0";
      const orderB = datasets$[b].order.get() ?? "a0";
      return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
    });
});

/** Favorited threads, newest first (threads have no fractional `order` key). */
const favoriteThreadIds$ = observable<string[]>(() => {
  const map = threads$.get() ?? {};
  return Object.values(map)
    .filter((thread) => thread.metadata?.favorite)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .map((thread) => thread.id);
});

/** True when at least one board, dataset, or thread is favorited. */
export const hasFavorites$ = observable(
  () =>
    favoriteBoardIds$.get().length > 0 ||
    favoriteDatasetIds$.get().length > 0 ||
    favoriteThreadIds$.get().length > 0,
);

/**
 * Combined Favorites list for the sidebar: favorited dashboards first, then
 * favorited datasets. Each entity keeps its intra-type `order`; drag-to-reorder
 * is intentionally inert here since boards and datasets occupy independent
 * fractional-index namespaces that cannot be merged without collisions.
 */
export const FavoritesList = () => {
  const isLoading = useValue(
    () =>
      !syncState(boards$).isLoaded.get() ||
      !syncState(datasets$).isPersistLoaded.get(),
  );
  const boardIds = useValue(favoriteBoardIds$);
  const datasetIds = useValue(favoriteDatasetIds$);
  const threadIds = useValue(favoriteThreadIds$);

  if (isLoading) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (
    boardIds.length === 0 &&
    datasetIds.length === 0 &&
    threadIds.length === 0
  )
    return null;

  return (
    <ScrollFade
      className="no-scrollbar -m-1 max-h-50 p-1"
      fromClassName="from-sidebar"
    >
      <SidebarMenu>
        {boardIds.map((id) => (
          <BoardItem key={id} id={id} />
        ))}
        {datasetIds.map((id) => (
          <DatasetItem key={id} id={id} />
        ))}
        {threadIds.map((id) => (
          <ThreadItem key={id} id={id} />
        ))}
      </SidebarMenu>
    </ScrollFade>
  );
};
